import { faCaretDown, faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ApplicationRoleCode,
    ICorporationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { CorporationDropdown } from 'components/dropdown/CorporationDropdown';
import { DropdownLabelRenderInfo } from 'components/dropdown/Dropdown';
import { AuthorizedImage } from 'components/image/AuthorizedImage';
import { useUser } from 'contexts/UserContext';
import { memo, useCallback } from 'react';
import styled from 'styled-components';
import { primaryText } from 'styles/theme';

export type CorporationSwitcherDropdownProps = {
    corporation: ICorporationResponse;
    className?: string;
};

const TriggerContent = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    color: ${primaryText};
    font-size: inherit;
    font-weight: inherit;
`;

const LogoImage = styled(AuthorizedImage)`
    max-height: 4.5rem;
    max-width: 12rem;
`;

const LogoWrapper = styled.span`
    display: inline-flex;
    height: 4rem;
    max-width: 12rem;
    align-items: center;
    justify-content: center;
`;

const ChevronIcon = styled(FontAwesomeIcon)<{ $isOpen: boolean }>`
    font-size: 1.2rem;
    margin-left: 0.2rem;
    transition: transform 0.2s ease;
    transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
`;

/**
 * Searchable Corporation switcher dropdown that displays in the dashboard header.
 */
export const CorporationSwitcherDropdown = memo(function CorporationSwitcherDropdown({
    corporation,
    className,
}: CorporationSwitcherDropdownProps) {
    const { user } = useUser();
    const userHasMultipleCorporations =
        user?.ApplicationRole !== ApplicationRoleCode.User ||
        user.Corporations.length > 0;

    const renderLabel = useCallback(
        ({ value, isOpen }: DropdownLabelRenderInfo<ICorporationResponse>) => {
            const corp = value ?? corporation;
            return (
                <TriggerContent>
                    <LogoWrapper>
                        <LogoImage
                            src={corp.IconUrl}
                            aria-label={`Logo for ${corp.Name}`}
                            alt={`${corp.Name} logo`}
                            className="corporation-logo"
                            fallback={<FontAwesomeIcon className="f-18" icon={faGlobe} />}
                            imageScaling="contain"
                        />
                    </LogoWrapper>
                    {corp.Name}
                    <ChevronIcon icon={faCaretDown} $isOpen={isOpen} />
                </TriggerContent>
            );
        },
        [corporation]
    );

    if (!userHasMultipleCorporations) {
        return (
            <>
                <LogoWrapper>
                    <LogoImage
                        src={corporation.IconUrl}
                        aria-label={`Logo for ${corporation.Name}`}
                        alt={`${corporation.Name} logo`}
                        className="corporation-logo"
                        fallback={
                            <FontAwesomeIcon
                                className="f-18 cursor-pointer"
                                icon={faGlobe}
                            />
                        }
                        imageScaling="contain"
                    />
                </LogoWrapper>
                {corporation.Name}
            </>
        );
    }

    return (
        <CorporationDropdown
            value={corporation}
            renderLabel={renderLabel}
            interactionMode="link"
            trigger={{
                className,
                hideChevron: true, // We're rendering our own
                ariaLabel: 'Switch corporation',
                paddingOverride: { x: 'none', y: 'none' },
                style: { alignItems: 'center', display: 'flex' },
            }}
        />
    );
});
